import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MarketId, MarketPriceState } from '@velocity-exchange/common';
import { orderbookQueryKeys } from './orderbookQueryKeys';
import { useOrderbookSubscriptionContext } from 'src/providers/orderbook/OrderbookSubscriptionProvider';

const FIVE_MINUTES_MS = 5 * 60 * 1000;

/**
 * Market price state data is synced from OrderbookSubscriptionProvider via queryClient.setQueryData.
 * This hook only reads from the cache - it does not fetch data itself.
 */
export function useMarketPriceState(marketId: MarketId | null) {
	const isProviderMounted = useOrderbookSubscriptionContext();
	const hasWarnedRef = useRef(false);

	useEffect(() => {
		if (
			process.env.NODE_ENV === 'development' &&
			!isProviderMounted &&
			!hasWarnedRef.current
		) {
			hasWarnedRef.current = true;
			console.warn(
				'useMarketPriceState: OrderbookSubscriptionProvider is not mounted. ' +
					'This hook will return null until the provider is available.'
			);
		}
	}, [isProviderMounted]);

	return useQuery<MarketPriceState | null>({
		queryKey: orderbookQueryKeys.marketPriceState(marketId?.key),
		queryFn: () => null,
		enabled: false,
		staleTime: Infinity,
		gcTime: FIVE_MINUTES_MS,
	});
}
